import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

interface ApiError {
  message: string;
  statusCode: number;
  code?: string;
  fields?: Array<{ field: string; message: string }>;
  errors?: Record<string, string[]>;
}

const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

/** Accepts "/admin/x", "admin/x" or a legacy "/api/admin/x" and returns a full URL. */
export function apiUrl(endpoint: string): string {
  let path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (API_BASE.endsWith('/api') && path.startsWith('/api/')) path = path.slice(4);
  return `${API_BASE}${path}`;
}

function authHeaders(): Record<string, string> {
  const { session } = useAdminAuthStore.getState();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

async function toApiError(response: Response): Promise<ApiError> {
  const data: any = await response.json().catch(() => ({}));
  const fallback =
    response.status === 401 ? 'Your session has expired. Please sign in again.'
    : response.status === 403 ? 'You do not have permission to do that.'
    : response.status === 404 ? 'Not found.'
    : response.status === 429 ? 'Too many requests. Please wait a moment and try again.'
    : response.status >= 500 ? 'Something went wrong on the server. Please try again.'
    : `Request failed (${response.status})`;
  return {
    message: data.error || data.message || fallback,
    statusCode: response.status,
    code: data.code,
    fields: data.fields,
    errors: data.errors,
  };
}

/**
 * Drop-in `fetch` for admin API calls: resolves the URL against VITE_API_URL
 * and always sends the signed-in admin's token (overriding any stale header).
 */
export function authFetch(endpoint: string, init: RequestInit = {}): Promise<Response> {
  const headers = { ...(init.headers as Record<string, string>), ...authHeaders() };
  return fetch(apiUrl(endpoint), { ...init, headers });
}

/** Authenticated JSON request. Unwraps `{ data }` unless the response is paginated (`data` + `meta`). */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...authHeaders(),
    ...(options.headers as Record<string, string>),
  };

  let response: Response;
  try {
    response = await fetch(apiUrl(endpoint), { ...options, headers });
  } catch {
    throw { message: 'Cannot reach the server. Check your connection.', statusCode: 0 } satisfies ApiError;
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const data = await response.json();
  if (data && typeof data === 'object' && Array.isArray(data.warnings) && data.warnings.length) {
    data.warnings.forEach((w: string) => toast.warning(w));
  }
  if (data?.data !== undefined && data?.meta !== undefined) return data as T;
  if (data?.data !== undefined && data?.summary !== undefined) return data as T;
  return data?.data !== undefined ? data.data : data;
}

/** Downloads a file (CSV export etc.) from an authenticated endpoint. */
export async function apiDownload(endpoint: string, filename: string, body?: unknown): Promise<void> {
  const response = await fetch(apiUrl(endpoint), {
    method: body === undefined ? 'GET' : 'POST',
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw await toApiError(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useApiQuery<T>(
  key: unknown[],
  endpoint: string,
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => apiRequest<T>(endpoint),
    ...options,
  });
}

export function useApiMutation<TData, TVariables = void>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string | ((variables: TVariables) => string),
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: ApiError, variables: TVariables) => void;
    invalidateKeys?: unknown[][];
    /** Show the server's error message as a toast when no onError is given (default true). */
    toastErrors?: boolean;
  }
) {
  const queryClient = useQueryClient();

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: (variables) => {
      const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint;
      return apiRequest<TData>(url, {
        method,
        body: variables !== undefined ? JSON.stringify(variables) : undefined,
      });
    },
    onSuccess: (data, variables) => {
      options?.invalidateKeys?.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
      options?.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (options?.onError) options.onError(error, variables);
      else if (options?.toastErrors !== false) toast.error(error.message || 'Something went wrong');
    },
  });
}

/** Upload file to R2 via presigned URL */
export async function uploadFileToR2(file: File, folder: string): Promise<string> {
  const { url: presignedUrl, key } = await apiRequest<{ url: string; key: string }>(
    `/upload/presign?fileName=${encodeURIComponent(file.name)}&folder=${encodeURIComponent(folder)}&contentType=${encodeURIComponent(file.type)}`,
    { method: 'POST' }
  );

  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!uploadResponse.ok) throw new Error('File upload failed');

  return `${import.meta.env.VITE_R2_PUBLIC_URL || 'https://cdn.wonderherbals.com'}/${key}`;
}

export type { ApiError };
